import {
  Avatar,
  Box,
  Button,
  Container,
  Grid,
  Paper,
  Typography,
} from "@mui/material";
import { useEffect, useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { RouteUrls } from "../../../Constants/RouteUrls";
import { FormDataType } from "DataTypes/Form";
import { commonFormSwitch, ControlledBoxSizeSwitch } from "Utils/SwitchCases";
import { HandleFormChange } from "Utils/Common";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import ControlledForm from "Components/ControlledForm";
import authApi from "Service/Auth.api";
import { registrationFormData } from "./Data";
import { useDispatch } from "react-redux";
import { setAlertData, startLoading, stopLoading } from "../../../Redux/Screen/Action";
import OtpInput from "Components/OtpInput";
import CountryCodeSelect from "Components/CountryCodeSelect";

const ProfessionalRegister = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [formData, setFormData] = useState<FormDataType[]>([]);

  // Validation Helpers
  const validateEmail = (email: string): boolean => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
  const validatePhone = (phone: string): boolean => /^\d{10}$/.test(phone);

  // OTP States
  const [emailData, setEmailData] = useState<{
    fieldVal: string;
    otpSend: boolean;
    otpVal: string;
    otpVerified: boolean;
  }>({
    fieldVal: "",
    otpSend: false,
    otpVal: "",
    otpVerified: false,
  });

  const [contactData, setContactData] = useState<{
    countryCode: string;
    fieldVal: string;
    otpSend: boolean;
    otpVal: string;
    otpVerified: boolean;
  }>({
    countryCode: "+91",
    fieldVal: "",
    otpSend: false,
    otpVal: "",
    otpVerified: false,
  });

  // Loading States
  const [emailLoading, setEmailLoading] = useState(false);
  const [contactLoading, setContactLoading] = useState(false);

  // Timers
  const [emailTimer, setEmailTimer] = useState(0);
  const emailTimerInterval = useRef<any>(null);

  const [contactTimer, setContactTimer] = useState(0);
  const contactTimerInterval = useRef<any>(null);

  // Initialize Form Data
  useEffect(() => {
    setFormData(JSON.parse(JSON.stringify(registrationFormData())));
  }, []);

  // Timer Cleanup
  useEffect(() => {
    return () => {
      if (emailTimerInterval.current) clearInterval(emailTimerInterval.current);
      if (contactTimerInterval.current) clearInterval(contactTimerInterval.current);
    }
  }, []);

  const startEmailTimer = () => {
    if (emailTimerInterval.current) clearInterval(emailTimerInterval.current);
    setEmailTimer(30);
    emailTimerInterval.current = setInterval(() => {
      setEmailTimer((prev) => {
        if (prev <= 1) {
          clearInterval(emailTimerInterval.current);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
  }

  const startContactTimer = () => {
    if (contactTimerInterval.current) clearInterval(contactTimerInterval.current);
    setContactTimer(30);
    contactTimerInterval.current = setInterval(() => {
      setContactTimer((prev) => {
        if (prev <= 1) {
          clearInterval(contactTimerInterval.current);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
  }

  const handleSendOtp = async (otpType: "email" | "contact") => {
    if (otpType === "email") {
      if (!validateEmail(emailData.fieldVal)) {
        dispatch(setAlertData({ alertMessage: "Please enter a valid email address first.", alertVariant: "error", openAlert: true }));
        return;
      }
      setEmailLoading(true);
      try {
        const res = await authApi.emailSendOtp({ email: emailData.fieldVal, requestType: "register" });
        if (res?.success) {
          setEmailData(prev => ({ ...prev, otpSend: true, otpVal: "", otpVerified: false }));
          startEmailTimer();
        }
      } finally {
        setEmailLoading(false);
      }
    } else {
      if (!contactData.countryCode || contactData.countryCode.trim() === "") {
        dispatch(setAlertData({ alertMessage: "Please enter country code.", alertVariant: "error", openAlert: true }));
        return;
      }
      if (!validatePhone(contactData.fieldVal)) {
        dispatch(setAlertData({ alertMessage: "Please enter a valid 10-digit contact number first.", alertVariant: "error", openAlert: true }));
        return;
      }
      const fullContactNumber = `${contactData.countryCode}${contactData.fieldVal}`.replace(/[^\d+]/g, '');
      setContactLoading(true);
      try {
        const res = await authApi.mobileSendOtp({ mobileNumber: fullContactNumber, requestType: "register" });
        if (res?.success) {
          setContactData(prev => ({ ...prev, otpSend: true, otpVal: "", otpVerified: false }));
          startContactTimer();
        }
      } finally {
        setContactLoading(false);
      }
    }
  };

  const handleVerifyOtp = async (otpType: "email" | "contact") => {
    if (otpType === "email") {
      if (emailData.otpVal.length !== 4) {
        dispatch(setAlertData({ alertMessage: "Please enter valid 4-digit OTP", alertVariant: "error", openAlert: true }));
        return;
      }
      setEmailLoading(true);
      try {
        const res = await authApi.verifyOtp({ mobileNumber: null, email: emailData.fieldVal, otp: emailData.otpVal });
        if (res?.success) {
          setEmailData(prev => ({ ...prev, otpVerified: true }));
          if (emailTimerInterval.current) clearInterval(emailTimerInterval.current);
          setEmailTimer(0);
        }
      } finally {
        setEmailLoading(false);
      }
    } else {
      if (contactData.otpVal.length !== 4) {
        dispatch(setAlertData({ alertMessage: "Please enter valid 4-digit OTP", alertVariant: "error", openAlert: true }));
        return;
      }
      setContactLoading(true);
      try {
        const fullContactNumber = `${contactData.countryCode}${contactData.fieldVal}`.replace(/[^\d+]/g, '');
        const res = await authApi.verifyOtp({ mobileNumber: fullContactNumber, email: null, otp: contactData.otpVal });
        if (res?.success) {
          setContactData(prev => ({ ...prev, otpVerified: true }));
          if (contactTimerInterval.current) clearInterval(contactTimerInterval.current);
          setContactTimer(0);
        }
      } finally {
        setContactLoading(false);
      }
    }
  };

  const handleRegister = async () => {
    console.log("formData", formData);
    // Basic validation for remaining fields
    if (!formData[0].value || !formData[1].value || !formData[7].value) {
      dispatch(setAlertData({ alertMessage: "Please fill all required fields.", alertVariant: "error", openAlert: true }));
      return;
    }

    const defaultDOB = new Date(new Date().setFullYear(new Date().getFullYear() - 18)).toISOString();

    const payload = {
      firstName: formData[0].value,
      lastName: formData[1].value,
      password: formData[7].value, // Using correct index for password if it moved
      email: emailData.fieldVal,
      contactNumber: contactData.fieldVal ? `${contactData.countryCode}${contactData.fieldVal}`.replace(/[^\d+]/g, '') : "0000000000",
      // Default values for fields removed from UI but likely required by backend
      address: "Not Provided",
      city: "Not Provided",
      state: "Not Provided",
      postalCode: "000000",
      dateOfBirth: defaultDOB,
      description: "New Professional",
      highestQualification: "Not Provided",
      profilePicturePath: "",
      roles: [],
      skills: [],
    };

    dispatch(startLoading({ loading: true, loadtext: "Registering..." }));

    try {
      const res = await authApi.registerProfessional(payload);
      if (res?.success) {
        window.location.href = "/home.html";
      }
    } catch (error) {
      console.error("Registration error", error);
    } finally {
      dispatch(stopLoading());
    }
  };

  const handlePasswordToggle = (fieldId: string) => {
    setFormData((prev) =>
      prev.map((field) =>
        field.id === fieldId
          ? { ...field, showPassword: !field.showPassword }
          : field
      )
    );
  };

  return (
    <Box
      sx={{
        minHeight: "100vh",
        display: "flex",
        flexDirection: "column",
        background: "linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%)",
        overflowY: "auto"
      }}
    >
      <Box sx={{ p: 3, display: "flex", justifyContent: "center" }}>
        <Box
          component="img"
          src={"https://nolegalpaisa.com/assets/images/new_logo.webp"}
          alt="Logo"
          sx={{ height: "60px", cursor: "pointer" }}
          onClick={() => (window.location.href = "/home.html")}
        />
      </Box>

      <Container maxWidth="sm" sx={{ pb: 8 }}>
        <Paper
          elevation={0}
          sx={{
            p: { xs: 3, md: 5 },
            borderRadius: 6,
            boxShadow: "0px 20px 50px rgba(0,0,0,0.08)",
            bgcolor: "rgba(255, 255, 255, 0.9)",
            backdropFilter: "blur(10px)",
            border: "1px solid rgba(255, 255, 255, 0.3)"
          }}
        >
          <Box component="header" sx={{ textAlign: "center", mb: 4 }}>
            <Avatar sx={{ m: "0 auto 16px", bgcolor: "primary.main", width: 60, height: 60, boxShadow: "0 8px 20px rgba(25, 118, 210, 0.2)" }}>
              <LockOutlinedIcon fontSize="large" />
            </Avatar>
            <Typography component="h1" variant="h4" fontWeight={800} sx={{ color: "#1a202c", mb: 1 }}>
              Professional Sign Up
            </Typography>
            <Typography variant="body1" color="text.primary">
              Create your account to get started
            </Typography>
          </Box>

          <Box component="form" noValidate>
            <Grid container spacing={2}>
              {/* Row 1: First Name & Last Name */}
              {formData.length > 0 && (
                <>
                  <Grid size={{ xs: 12, md: 12 }} sx={{ pl: 1 }}>
                    {commonFormSwitch({
                      formObj: formData[0],
                      onChange: (data) => setFormData(HandleFormChange(data, formData)),
                    })}
                  </Grid>
                  <Grid size={{ xs: 12, md: 12 }} sx={{ pl: 1 }}>
                    {commonFormSwitch({
                      formObj: formData[1],
                      onChange: (data) => setFormData(HandleFormChange(data, formData)),
                    })}
                  </Grid>
                </>
              )}

              {/* Row 2: Email & OTP */}
              <Grid size={{ xs: 12, md: 12 }}>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                  <Box sx={{ width: '100%' }}>
                    <ControlledForm.Input
                      id="email"
                      value={emailData.fieldVal}
                      {...(emailData.otpVerified && {
                        focused: true,
                        borderColor: "success",
                        readOnly: true,
                      })}
                      label="Email Address"
                      onChange={(value) =>
                        setEmailData({ ...emailData, fieldVal: value as string })
                      }
                    />
                  </Box>

                  {!emailData.otpVerified && (
                    <Box sx={{ width: '100%' }}>
                      {emailData.otpSend ? (
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, alignItems: 'center' }}>
                          <OtpInput
                            length={4}
                            value={emailData.otpVal}
                            onChange={(val) => setEmailData(prev => ({ ...prev, otpVal: val }))}
                            onComplete={() => { }}
                          />
                          <Box sx={{ display: 'flex', gap: 1, width: '100%', justifyContent: 'center' }}>
                            <Button
                              variant="contained"
                              size="small"
                              onClick={() => handleVerifyOtp("email")}
                              disabled={emailLoading}
                              sx={{ textTransform: 'none', flex: 1 }}
                            >
                              Verify
                            </Button>
                            <Button
                              disabled={emailTimer > 0 || emailLoading || !validateEmail(emailData.fieldVal)}
                              onClick={() => handleSendOtp("email")}
                              sx={{ textTransform: 'none', fontSize: '0.75rem', flex: 1 }}
                              size="small"
                              variant="outlined"
                            >
                              {emailTimer > 0 ? `${emailTimer}s` : "Resend"}
                            </Button>
                          </Box>
                        </Box>
                      ) : (
                        <Button
                          fullWidth
                          variant="contained"
                          sx={{ height: '48px', textTransform: 'none' }}
                          onClick={() => handleSendOtp("email")}
                          disabled={emailLoading || !validateEmail(emailData.fieldVal)}
                        >
                          Send OTP
                        </Button>
                      )}
                    </Box>
                  )}
                  {emailData.otpVerified && (
                    <Box sx={{ width: '100%', textAlign: 'left' }}>
                      <Typography color="success.main" variant="body2" fontWeight="bold">Verified</Typography>
                    </Box>
                  )}
                </Box>
              </Grid>

              {/* Row 3: Phone & OTP */}
              <Grid size={{ xs: 12, md: 12 }}>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                  <Box sx={{ display: 'flex', width: '100%', gap: 1 }}>
                    <Box sx={{ width: '30%' }}>
                      <CountryCodeSelect 
                        value={contactData.countryCode} 
                        disabled={contactData.otpVerified}
                        onChange={(val) => {
                          setContactData({ ...contactData, countryCode: val });
                        }} 
                      />
                    </Box>
                    <Box sx={{ width: '70%' }}>
                      <ControlledForm.Input
                        id="contact"
                        value={contactData.fieldVal}
                        {...(contactData.otpVerified && {
                          focused: true,
                          borderColor: "success",
                          readOnly: true,
                        })}
                        label="Contact Number"
                        onChange={(value) => {
                          const val = value as string;
                          if (val.length <= 10 && /^\d*$/.test(val)) {
                            setContactData({ ...contactData, fieldVal: val as string });
                          }
                        }}
                      />
                    </Box>
                  </Box>

                  {!contactData.otpVerified && (
                    <Box sx={{ width: '100%' }}>
                      {contactData.otpSend ? (
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, alignItems: 'center' }}>
                          <OtpInput
                            length={4}
                            value={contactData.otpVal}
                            onChange={(val) => setContactData(prev => ({ ...prev, otpVal: val }))}
                            onComplete={() => { }}
                          />
                          <Box sx={{ display: 'flex', gap: 1, width: '100%', justifyContent: 'center' }}>
                            <Button
                              variant="contained"
                              size="small"
                              onClick={() => handleVerifyOtp("contact")}
                              disabled={contactLoading}
                              sx={{ textTransform: 'none', flex: 1 }}
                            >
                              Verify
                            </Button>
                            <Button
                              disabled={contactTimer > 0 || contactLoading || !validatePhone(contactData.fieldVal)}
                              onClick={() => handleSendOtp("contact")}
                              sx={{ textTransform: 'none', fontSize: '0.75rem', flex: 1 }}
                              size="small"
                              variant="outlined"
                            >
                              {contactTimer > 0 ? `${contactTimer}s` : "Resend"}
                            </Button>
                          </Box>
                        </Box>
                      ) : (
                        <Button
                          fullWidth
                          variant="contained"
                          sx={{ height: '48px', textTransform: 'none' }}
                          onClick={() => handleSendOtp("contact")}
                          disabled={contactLoading || !validatePhone(contactData.fieldVal)}
                        >
                          Send OTP
                        </Button>
                      )}
                    </Box>
                  )}
                  {contactData.otpVerified && (
                    <Box sx={{ width: '100%', textAlign: 'left' }}>
                      <Typography color="success.main" variant="body2" fontWeight="bold">Verified</Typography>
                    </Box>
                  )}
                </Box>
              </Grid>

              {/* Row 4: Password */}
              {formData.length > 7 && (
                <Grid size={{ xs: 12, md: 12 }}>
                  {commonFormSwitch({
                    formObj: {
                      ...formData[7], // Ensure correct index for password
                      onPasswordToggle: formData[7].hasPasswordToggle
                        ? () => handlePasswordToggle(formData[7].id)
                        : undefined,
                    },
                    onChange: (data) => setFormData(HandleFormChange(data, formData)),
                  })}
                </Grid>
              )}
            </Grid>
            {/* Submit Button */}
            <Grid size={{ xs: 12, md: 12 }} sx={{ mt: 2 }}>
              <Button
                fullWidth
                variant="contained"
                size="large"
                sx={{ py: 2, fontSize: '1.1rem', fontWeight: "bold", borderRadius: 3 }}
                onClick={handleRegister}
                disabled={!emailData.otpVerified || !contactData.otpVerified}
              >
                Confirm Registration
              </Button>
            </Grid>

            <Grid size={{ xs: 12, md: 12 }}>
              <Typography
                sx={{
                  textAlign: "center",
                  mt: 2,
                  color: "text.primary"
                }}
                variant="body2"
              >
                Already have an account?{" "}
                <Typography
                  component="span"
                  variant="body2"
                  sx={{
                    cursor: "pointer",
                    color: "primary.main",
                    fontWeight: 700,
                    textDecoration: "underline"
                  }}
                  onClick={() => navigate(`/${RouteUrls.login}`)}
                >
                  Login
                </Typography>
              </Typography>
            </Grid>

          </Box>
        </Paper>
      </Container>
    </Box>
  );
};
export default ProfessionalRegister;
